import { plural } from '../home/attention';
import { entityPageOf } from '../scorecards/scorecards';

/**
 * The security findings as the github-insights backend answers them (DX-587,
 * specs/security-findings.md; where each source is read and how, is
 * docs/security-findings.md).
 *
 * The Security page, a component's Security tab and its facts bar all read
 * them from there rather than counting anything themselves, so the three cannot
 * disagree. Nothing here names a source: what a source is called, and how the
 * tab heads its findings, arrives with the answer.
 */

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low'];

export type LayerId = 'dependencies' | 'codePatterns' | 'threat';

export type VerdictMark = '🔴' | '🟠' | '🟡' | '🟢';
export const VERDICT_MARKS: VerdictMark[] = ['🔴', '🟠', '🟡', '🟢'];

export type Finding = {
  source: string;
  layer: LayerId;
  /** Null: measured with nothing open — a verdict of "Unbedenklich". */
  severity: Severity | null;
  title: string;
  identifier: string;
  state: 'open' | 'accepted';
  openSince: string | null;
  link: string | null;
  component: string;
  /** The source's own word for it, shown as written. */
  verdict?: { mark: VerdictMark; text: string; caveat?: string };
  facts: { label: string; value: string }[];
};

export type SourceInfo = {
  id: string;
  label: string;
  layer: LayerId;
  titleLabel: string;
  sinceLabel: string;
};

export type SourceState = SourceInfo & {
  state: 'pending' | 'answered' | 'partial' | 'failed';
  readAt: string | null;
  unread: number;
  reason?: string;
};

export type LayerCell =
  | { state: 'uncovered' }
  | { state: 'unmeasured'; reasons: string[] }
  | {
      state: 'read';
      open: number;
      bySeverity: Record<Severity, number>;
      byMark: Partial<Record<VerdictMark, number>>;
      measured: number;
      notMeasured: number;
      reasons: string[];
    };

export type ComponentSecurity = {
  entityRef: string;
  name: string;
  title?: string;
  type?: string;
  owner?: { ref: string; title?: string };
  repository: string;
  status: 'open' | 'none' | 'unmeasured';
  open: number;
  worst: { severity: Severity; count: number } | null;
  oldestOpen: string | null;
  layers: Record<LayerId, LayerCell>;
  readings: {
    source: string;
    state: 'read' | 'unmeasured';
    reason?: string;
    failed?: boolean;
    link?: string;
  }[];
  findings: Finding[];
  notMeasured: { source: string; title: string; identifier: string }[];
};

export type LayerSummary = {
  sources: string[];
  components: number;
  open: number;
  bySeverity: Record<Severity, number>;
  byMark: Partial<Record<VerdictMark, number>>;
  measured: number;
};

export type Layer = { id: LayerId; title: string };

export type SecuritySnapshot = {
  generatedAt: string | null;
  layers: Layer[];
  sources: SourceState[];
  components: ComponentSecurity[];
  summary: {
    components: number;
    measured: number;
    layers: Record<LayerId, LayerSummary>;
  };
};

/** One component's findings, for its Security tab and its facts bar. */
export type ComponentSecurityAnswer = Omit<
  SecuritySnapshot,
  'components' | 'summary'
> & { component: ComponentSecurity | null };

/**
 * The backend's refusal of a sign-in that names no team (AC-12), told apart
 * from a failure so the page can say why and lead to the Access page.
 */
export const isNoTeam = (body: unknown): boolean =>
  (body as { reason?: unknown } | undefined)?.reason === 'no-team';

/** Where a colleague in no team learns how to join one. */
export const ACCESS_PAGE = '/access';

/** A component's Security tab. */
export const securityTabOf = (entityRef: string): string =>
  `${entityPageOf(entityRef)}/security`;

/** An open finding: one with a severity, not accepted. */
export const isOpen = (finding: Finding): boolean =>
  finding.severity !== null && finding.state === 'open';

/**
 * How a severity looks: one red from dark to light, so the order reads by
 * lightness rather than by hue alone (the tokens in theme/tokens.css, one ramp
 * per appearance), and a shape of its own, so it reads without colour too. The
 * word always stands beside them, as the advisory or the report gives it.
 */
export const SEVERITY_COLOUR: Record<Severity, string> = {
  critical: 'var(--ui-sev-critical)',
  high: 'var(--ui-sev-high)',
  medium: 'var(--ui-sev-medium)',
  low: 'var(--ui-sev-low)',
};

/** A shape, drawn the same size whatever the font holds. */
export type ShapeName = 'triangle' | 'diamond' | 'circle' | 'ring' | 'check';

export const SEVERITY_SHAPE: Record<Severity, ShapeName> = {
  critical: 'triangle',
  high: 'diamond',
  medium: 'circle',
  low: 'ring',
};

/** A verdict looks like the severity it counts as; "Unbedenklich" is a tick. */
export const MARK_COLOUR: Record<VerdictMark, string> = {
  '🔴': SEVERITY_COLOUR.critical,
  '🟠': SEVERITY_COLOUR.high,
  '🟡': SEVERITY_COLOUR.medium,
  '🟢': 'var(--ui-sev-none)',
};

export const MARK_SHAPE: Record<VerdictMark, ShapeName> = {
  '🔴': SEVERITY_SHAPE.critical,
  '🟠': SEVERITY_SHAPE.high,
  '🟡': SEVERITY_SHAPE.medium,
  '🟢': 'check',
};

/** The verdicts as the analyses name them, for a reader of the legend. */
export const MARK_WORD: Record<VerdictMark, string> = {
  '🔴': 'Nicht freigeben / Sofort handeln',
  '🟠': 'Handlungsbedarf',
  '🟡': 'Vertretbar',
  '🟢': 'Unbedenklich',
};

const DAY_MS = 86_400_000;

/** How long ago, in whole days: "today", "1 day", "41 days". */
export function ageOf(iso: string, now: number): string {
  const days = Math.floor((now - Date.parse(iso)) / DAY_MS);
  if (!Number.isFinite(days) || days < 1) return 'today';
  return plural(days, 'day', 'days');
}

/** How many open findings, by severity, in the words a pill shows. */
export const severityCounts = (
  bySeverity: Record<Severity, number>,
): { severity: Severity; count: number }[] =>
  SEVERITIES.filter(s => bySeverity[s] > 0).map(severity => ({
    severity,
    count: bySeverity[severity],
  }));

/** The most severe of what is counted. */
export const worstOf = (
  bySeverity: Record<Severity, number>,
): Severity | undefined => SEVERITIES.find(s => bySeverity[s] > 0);

/**
 * What a source that did not fully answer means for the page, in one
 * sentence; undefined when it answered.
 */
export function sourceNotice(
  source: SourceState,
  components: ComponentSecurity[] = [],
): string | undefined {
  switch (source.state) {
    case 'pending':
      return `${source.label} has not been read since the portal started, so its findings are not counted yet.`;
    case 'failed':
      return `${source.label} could not be read, so what it covers shows as “Not measured” rather than as 0.`;
    case 'partial': {
      // Named when they are few, so a reader can find them in the table.
      const unread = components
        .filter(c =>
          c.readings.some(r => r.source === source.id && r.failed === true),
        )
        .map(c => c.title ?? c.name);
      const which =
        unread.length > 0 && unread.length <= 3
          ? unread.join(', ').replace(/, ([^,]*)$/, ' and $1')
          : plural(source.unread, 'component', 'components');
      return `${source.label} could not read ${which}, so ${
        source.unread === 1 ? 'it shows' : 'they show'
      } as “Not measured” rather than as 0.`;
    }
    default:
      return undefined;
  }
}

/** What the page says about a layer no source reports yet. */
export const unreportedNotice = (layer: Layer): string =>
  `No source reports ${layer.title.toLowerCase()} to the portal yet, so “${
    layer.title
  }” counts nothing. That is not the same as nothing found.`;

/**
 * The notices in one line, for while they are folded (AC-6): every source
 * that did not answer in full, and every layer nothing reports, by name.
 */
export function noticesSummary(
  sources: SourceState[],
  unreported: Layer[],
): string {
  const parts = [
    ...sources.flatMap(source => {
      switch (source.state) {
        case 'pending':
          return [`${source.label} has not been read yet`];
        case 'failed':
          return [`${source.label} could not be read`];
        case 'partial':
          return [
            `${source.label} could not read ${plural(
              source.unread,
              'component',
              'components',
            )}`,
          ];
        default:
          return [];
      }
    }),
    ...unreported.map(
      layer => `No source reports ${layer.title.toLowerCase()} yet`,
    ),
  ];
  // A source's label stands as the backend gives it, as in its notice.
  return parts.join(' · ');
}

/**
 * The tab's count (AC-9): the open findings; none when none is open, and a
 * question mark when it is not measured, as its facts bar says.
 */
export function tabCountOf(
  component: ComponentSecurity | null | undefined,
): number | string | undefined {
  if (!component) return undefined;
  if (component.status === 'open') return component.open;
  if (component.status === 'unmeasured') return '?';
  return undefined;
}

/** The first reason a component is not measured, in a sentence. */
export const unmeasuredReasonOf = (
  component: ComponentSecurity,
): string | undefined =>
  component.readings.find(r => r.state === 'unmeasured')?.reason ??
  Object.values(component.layers).flatMap(cell =>
    cell.state === 'unmeasured' ? cell.reasons : [],
  )[0];
